// adminAlerts - the words and rules of the admin's alerts on a saved pass
// (0064, docs/todos/0064-durable-run-execution-and-orphan-reaper.md; founder
// rulings, 2026-10-26: the admin is alerted when a pass pauses, on an Alerts
// page of its own, and the daily reminder says how long the pass has waited).
// The founder app shows a saved pass as running, so only the admin can notice
// it; GET /api/v1/admin/alerts serves the open alerts, which the Alerts page
// lists, the header's "Alerts" link counts and the browser notifier announces.
// The alert lines, the count's spoken name, the page's heading and empty state
// and the Settings row's words live here. The reason words live in
// lib/pauseWords.ts; the page's column labels and its loading and failure
// lines are in views/Alerts.tsx, as on the other admin pages, and the failure
// line's fallback message is in hooks/useAdminAlerts.ts.
import type { paths } from "../api/types";
import { pauseReasonWords } from "./pauseWords";

type AdminAlertsJSON = NonNullable<
  paths["/api/v1/admin/alerts"]["get"]["responses"]["200"]["content"]["application/json"]
>;

// AdminAlert is one open alert as the server answers it.
export type AdminAlert = AdminAlertsJSON["alerts"][number];

export const ALERTS_HEADING = "Alerts";
export const NO_ALERTS = "No open alerts.";

// The Settings row that asks this browser for notification permission.
export const BROWSER_ALERTS_HEADING = "Browser alerts";
export const ENABLE = "Enable";
export const ON = "On";
export const BLOCKED = "Blocked by this browser";
export const UNSUPPORTED = "Not supported by this browser";

const DAY_MS = 86_400_000;

// alertKey names one alert: its run and when it was raised, so a daily
// reminder is a new alert of the same run.
export function alertKey(a: AdminAlert): string {
  return `${a.run_id}|${a.alerted_at}`;
}

// alertDays is how many whole days the pass had waited since this stop when
// the alert was raised, at least 1.
export function alertDays(a: AdminAlert): number {
  return Math.max(1, Math.floor((Date.parse(a.alerted_at) - Date.parse(a.stopped_at)) / DAY_MS));
}

// alertTitle is an alert's line: "Paused: <reason>" for the first alert,
// "Still paused <N> days: <reason>" for a daily reminder.
export function alertTitle(a: AdminAlert): string {
  const reason = pauseReasonWords(a.reason);
  if (!a.reminder) return `Paused: ${reason}`;
  const days = alertDays(a);
  return `Still paused ${days} ${days === 1 ? "day" : "days"}: ${reason}`;
}

// alertProduct is the product's name, else the first 8 characters of its id.
export function alertProduct(a: AdminAlert): string {
  return a.product_name || a.product_id.slice(0, 8);
}

// alertDate is a date as "Oct 30".
export function alertDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// alertBody is the line under an alert: the product and when the pass closes.
export function alertBody(a: AdminAlert): string {
  return `${alertProduct(a)} · closes ${alertDate(a.closes_at)}`;
}

// pausedCountName is the spoken name of the header's count.
export function pausedCountName(n: number): string {
  return n === 1 ? "1 paused run" : `${n} paused runs`;
}

// shouldNotifyAdmin is the gate for a browser notification of a new alert:
// this browser granted permission, the tab is hidden (a visible tab shows the
// count), and this alert has not been announced. The founder app's run
// completion gate without its stored preference: the admin opts in by
// granting the browser's permission.
export function shouldNotifyAdmin(opts: {
  permission: NotificationPermission;
  documentHidden: boolean;
  alreadyNotified: boolean;
}): boolean {
  return opts.permission === "granted" && opts.documentHidden && !opts.alreadyNotified;
}
